import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { _className, Button, ComponentSync, EditableItem, LL_H_C, LL_V_L, ModuleFE_Toaster, Show, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import { _entries, capitalizeAllFirstLetters, filterInstances, MUSTNeverHappenException, removeFromArray, Second, sortArray } from '@nu-art/ts-common';
import './Component_DispositionForm.scss';
import { DispositionRecommendedAction } from '@app/advisor-shared';
import { ModuleFE_Advisor_DiseaseProfile, ModuleFE_EMInterface, ModuleFE_EncounterMetadata, ModuleFE_EncounterStorage, ModuleFE_RuntimeProtocol } from '@app/advisor-frontend';
import { Renderer_DispoDiagnosis } from './renderers/Renderer_DispoToggler/Renderer_DispoDiagnosis.js';
import { AdvisorDropdown_DiseaseProfile } from '../AdvisorUiComponents/AdvisorUiComponents.js';
import { FadeScrollWrapper, ICONS } from '@app/styles';
import { ModuleFE_ScrollBar } from '@app/styles/frontend/modules/ModuleFE_ScrollBar/ModuleFE_ScrollBar';
import { ModuleFE_Spotlight } from '../../../modules/ModuleFE_Spotlight/index.js';
import { Component_SmartNote } from '../physician-notes/Component_SmartNote.js';
import { DispoTypeToHeader } from '../../panels/AdvisorPanel_Spotlight/consts.js';
import { ModuleFE_AdvisorMini } from '../../../modules/ModuleFE_AdvisorMini.js';
import { ModuleFE_ProtocolInteraction } from '../../../_enum/protocol-interaction/index.js';
import { defaultColumnWidthUpdate, MiddleColumn_TwoThirdsRight, PanelKey_Home } from '@app/advisor-v5-shared';
import { ModuleFE_AdvisorBoard } from '../../../_enum/advisor-board/index.js';
const Const_DispositionFormData = 'storage-disposition-form';
export class Component_DispositionForm extends ComponentSync {
    collapseAbleRef = React.createRef();
    // ######################### Life Cycle #########################
    __onPathwayEndUpdated() {
        // if no protocol has ended ignore
        if (!ModuleFE_EMInterface.isPostDisposition())
            return;
        // update the component
        this.forceUpdate();
    }
    deriveStateFromProps(nextProps, state) {
        state = this.state ? { ...this.state } : (this.getDispositionFormData().get() ?? {});
        state.dispositionType = nextProps.dispositionType;
        const diseaseId = this.deriveDiseaseFromProtocol(nextProps.protocolId);
        if (diseaseId) {
            state.defaultDiagnosis ??= diseaseId;
        }
        else {
            delete state.defaultDiagnosis;
            delete state.otherDiagnoses;
        }
        state.otherDiagnoses = filterInstances(nextProps.additionalProtocolIds?.map(protocolId => this.deriveDiseaseFromProtocol(protocolId))) ?? [];
        state.submitted = nextProps.submitted;
        state.physicianNote = new EditableItem(nextProps.physicianNote).setSaveAction(async (note) => note);
        state.physicianNotesExpanded ??= false;
        state.physicianNoteEditMode ??= false;
        // save latest state to storage
        this.getDispositionFormData().set(state);
        return state;
    }
    componentDidMount() {
        this.setCollapseAbleMaxHeight();
    }
    componentWillUnmount() {
        ModuleFE_ProtocolInteraction.closeDisposition();
        ModuleFE_AdvisorBoard.updateColumnsWidth(defaultColumnWidthUpdate);
    }
    // ######################### Logic #########################
    deriveDiseaseFromProtocol(protocolId) {
        const protocol = ModuleFE_RuntimeProtocol.cache.unique(protocolId);
        if (!protocol)
            return;
        if (protocol.variableIds.length === 1)
            return protocol.variableIds[0];
        const scoreMap = _entries(protocol.careMapOutcomes ?? {}).reduce((map, entry) => {
            map[entry.key] = entry.value.normalizedScore;
            return map;
        }, {});
        const varIds = sortArray([...protocol.variableIds], id => scoreMap[id], true);
        return varIds[0];
    }
    getDispositionFormData() {
        return ModuleFE_EncounterStorage.getStorageKey(Const_DispositionFormData);
    }
    setCollapseAbleMaxHeight = () => {
        const el = this.collapseAbleRef.current;
        if (!el)
            return;
        el.style.setProperty('--max-height', `${el.scrollHeight}px`);
    };
    closeDispositionForm = () => {
        // on cancel clear context
        this.getDispositionFormData().delete();
        const isMini = ModuleFE_AdvisorMini.getIsMiniActive();
        if (!this.props.protocolId) {
            if (isMini)
                return ModuleFE_AdvisorMini.openPanel(PanelKey_Home);
            return ModuleFE_Spotlight.closeSpotlight();
        }
        ModuleFE_ProtocolInteraction.closeDisposition(true);
        if (isMini)
            ModuleFE_AdvisorMini.openPanel(PanelKey_Home);
    };
    submitDispoForm = async () => {
        try {
            const smartNote = this.state.physicianNote.item;
            //Submit the summary note for write back
            await ModuleFE_EMInterface.vv1.smartNote.writeBack({ note: smartNote }).executeSync();
            //Submit the endEncounter request to end the encounter
            await ModuleFE_EMInterface.vv1.encounter.endEncounter({
                diagnosisVariable: this.state.defaultDiagnosis,
                dispositionAction: this.props.dispositionType,
                additionalDiagnoses: this.state.otherDiagnoses,
            }).executeSync();
            //Save dispo data to storage
            this.getDispositionFormData().set(this.state);
            if (!smartNote.templateId)
                this.logError('Could not save smart note to storage, smart note did not have a template id');
            else {
                const noteStorageKey = ModuleFE_EMInterface.getSmartNoteStorageKey(smartNote.templateId);
                noteStorageKey.set(smartNote);
            }
            // open the user survey if it's configured for the currently used ehr instance
            if (ModuleFE_EncounterMetadata.getEncounterMetadata()?.advisorConfig.enableSurvey) {
                return setTimeout(() => ModuleFE_Spotlight.openSurvey(), 100);
            }
        }
        catch (err) {
            this.logError(err);
        }
    };
    saveNote = async () => {
        const note = this.state.physicianNote.item;
        note.savedByPhysician = true;
        try {
            await ModuleFE_EMInterface.vv1.smartNote.save({ note }).executeSync();
            ModuleFE_Toaster.toastSuccess('Summary note saved!', 2 * Second);
            this.setState({ physicianNoteEditMode: false }, () => this.setNoteExpandState(false));
        }
        catch (err) {
            this.logError(err);
            ModuleFE_Toaster.toastError('Failed saving summary note, try again later', 2 * Second);
        }
    };
    toggleNoteEditMode = () => {
        const nextMode = !this.state.physicianNoteEditMode;
        this.setState({ physicianNoteEditMode: nextMode }, () => {
            if (!nextMode)
                return;
            this.setNoteExpandState(true);
        });
    };
    setNoteExpandState = (expandState = !this.state.physicianNotesExpanded) => {
        ModuleFE_AdvisorBoard.updateColumnsWidth(expandState ? MiddleColumn_TwoThirdsRight : defaultColumnWidthUpdate);
        this.setState({ physicianNotesExpanded: expandState });
    };
    // ######################### Render #########################
    render() {
        const isSubmitted = ModuleFE_EMInterface.isPostDisposition();
        return _jsxs(LL_V_L, { className: 'disposition-form-v2', children: [_jsxs(Show, { children: [_jsx(Show.If, { condition: isSubmitted, children: this.renderEndScreen() }), _jsx(Show.Else, { children: this.renderForm() })] }), this.renderControls()] });
    }
    renderControls = () => {
        const isSubmitted = ModuleFE_EMInterface.isPostDisposition();
        return _jsx(LL_H_C, { className: 'disposition-form-v2__controls', children: _jsxs(Show, { children: [_jsx(Show.If, { condition: !isSubmitted, children: _jsx(Button, { variant: 'secondary', onClick: this.closeDispositionForm.bind(this), children: "Cancel" }) }), _jsx(Show.If, { condition: !isSubmitted && this.state.physicianNoteEditMode, children: _jsx(Button, { variant: 'primary', onClick: this.saveNote, children: "Save" }, 'save') }), _jsx(Show.If, { condition: !isSubmitted, children: _jsx(Button, { variant: 'primary', onClick: this.submitDispoForm, children: "Submit" }, 'done') }), _jsx(Show.If, { condition: isSubmitted, children: _jsx(Button, { variant: 'primary', onClick: this.closeDispositionForm, children: "Close" }, 'close') })] }) });
    };
    // ######################### Render - Form #########################
    renderForm = () => {
        return _jsxs(FadeScrollWrapper, { scrollRef: this.props.scrollRef, className: _className('form-container', this.state.physicianNotesExpanded && 'collapsed'), children: [this.renderDiagnosisSection(), this.renderPhysicianNotes()] });
    };
    renderDiagnosisSection = () => {
        if (this.state.dispositionType === DispositionRecommendedAction.ENGAGED_ENCOUNTER)
            return;
        return _jsx(LL_V_L, { innerRef: this.collapseAbleRef, className: 'form-collapsables', children: _jsxs(TS_PropRenderer.Vertical, { label: 'Diagnosis', className: 'diagnosis-section', children: [this.renderWorkingDiagnosis(), this.renderAdditionalDiagnoses()] }) });
    };
    renderWorkingDiagnosis = () => {
        const dp = ModuleFE_Advisor_DiseaseProfile.cache.find(dp => dp.variableId === this.state.defaultDiagnosis);
        const onDelete = this.props.protocolId ? undefined : () => {
            this.setState({ defaultDiagnosis: undefined }, () => {
                this.setCollapseAbleMaxHeight();
                ModuleFE_ScrollBar.updateScrollBar(this.props.scrollRef);
                this.getDispositionFormData().set(this.state);
            });
        };
        return _jsx(TS_PropRenderer.Vertical, { className: 'sub-diagnosis-section', label: 'Differentials & Workup', children: dp ? _jsx(Renderer_DispoDiagnosis, { diagnosis: dp, onDelete: onDelete }) :
                _jsx(AdvisorDropdown_DiseaseProfile.selectable, { onSelected: dp => {
                        this.setState({ defaultDiagnosis: dp.variableId }, () => {
                            this.setCollapseAbleMaxHeight();
                            ModuleFE_ScrollBar.updateScrollBar(this.props.scrollRef);
                            this.getDispositionFormData().set(this.state);
                        });
                    } }) });
    };
    renderAdditionalDiagnoses = () => {
        return _jsxs(TS_PropRenderer.Vertical, { className: 'sub-diagnosis-section', label: 'Additional Diagnoses', children: [_jsx(AdvisorDropdown_DiseaseProfile.selectable, { onSelected: (dp) => {
                        this.state.otherDiagnoses?.push(dp.variableId);
                        this.forceUpdate(() => {
                            this.setCollapseAbleMaxHeight();
                            ModuleFE_ScrollBar.updateScrollBar(this.props.scrollRef);
                            this.getDispositionFormData().set(this.state);
                        });
                    }, queryFilter: item => !this.state.otherDiagnoses?.includes(item._id) }), this.state.otherDiagnoses?.map(dpVariableId => {
                    const dp = ModuleFE_Advisor_DiseaseProfile.cache.find(dp => dp.variableId === dpVariableId);
                    if (!dp)
                        throw new MUSTNeverHappenException(`cannot find dp with variable id ${dpVariableId}`);
                    return _jsx(Renderer_DispoDiagnosis, { diagnosis: dp, onDelete: () => {
                            removeFromArray(this.state.otherDiagnoses, item => item === dp.variableId);
                            this.forceUpdate(() => {
                                this.setCollapseAbleMaxHeight();
                                ModuleFE_ScrollBar.updateScrollBar(this.props.scrollRef);
                                this.getDispositionFormData().set(this.state);
                            });
                        } }, dpVariableId);
                })] });
    };
    renderPhysicianNotes = () => {
        return _jsxs(TS_PropRenderer.Vertical, { className: _className('form-physician-notes', this.state.physicianNotesExpanded && 'expanded'), label: _jsxs(_Fragment, { children: ["Physician Notes", this.render_PhysicianNotes_HeaderTail()] }), children: [_jsx("span", { className: 'disposition-form__subtitle', children: "After submission, you can edit in your Notes tab." }), _jsx(Component_SmartNote, { note: this.state.physicianNote, asTextBlob: !this.state.physicianNoteEditMode, editMode: this.state.physicianNoteEditMode })] });
    };
    render_PhysicianNotes_HeaderTail = () => {
        return _jsxs(LL_H_C, { style: { gap: '10px' }, children: [_jsx(ICONS.advisor_v5_freeText.component, { onClick: () => this.toggleNoteEditMode(), className: _className('notes-edit-button', this.state.physicianNoteEditMode && 'active') }), !ModuleFE_AdvisorMini.getIsMiniActive() && _jsx(ICONS.advisor_v5_expand.component, { onClick: () => this.setNoteExpandState() })] });
    };
    // ######################### Render - End Screen #########################
    renderEndScreen = () => {
        const dp = ModuleFE_Advisor_DiseaseProfile.cache.find(dp => dp.variableId === this.state.defaultDiagnosis);
        const title = !dp ? 'Encounter Completed!' : `${capitalizeAllFirstLetters(dp.label)} Protocol Completed!`;
        const row2 = `the summary note was sent to the EHR. Please refresh the smart link in your ED Provider Note to pull it into your note.`;
        const flowType = DispoTypeToHeader[this.state.dispositionType];
        return _jsxs(_Fragment, { children: [_jsx("span", { className: 'disposition-form-v2__end-row', children: title }), _jsxs(LL_V_L, { className: 'disposition-form-v2__end-notes', children: [_jsx("span", { className: 'disposition-form-v2__end-row', children: "Please note:" }), _jsx("ul", { children: _jsx("li", { className: 'disposition-form-v2__end-notes__note', children: row2 }) })] }), this.state.dispositionType !== DispositionRecommendedAction.ENGAGED_ENCOUNTER && _jsxs("span", { className: 'disposition-form-v2__end-row', children: ["Please go to the ", _jsx("b", { children: "Dispo" }), " tab to continue the ", flowType, " flow."] })] });
    };
}
//# sourceMappingURL=Component_DispositionForm.js.map